import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ErroApi } from '../lib/api';
import { useAutenticacao } from '../lib/auth';
import { criarFuncao, removerFuncao } from '../lib/escalas';
import {
  buscarCalendarioLideranca,
  buscarCultoLideranca,
  buscarEscalaLideres,
  buscarLideres,
  criarContaDeLider,
  oQueLidera,
  type CultoLideranca,
  type Lider,
  type MinisterioLideranca,
} from '../lib/escalaLideres';
import { diaDaSemana, diaSP, horaSP, mesCurto, numeroDoDia, quandoEmPalavras } from '../lib/datas';
import { Alerta, Botao, Cartao, Modal } from '../componentes/Interface';
import { useConfirmacao } from '../componentes/Confirmacao';
import CalendarioEscalas from '../componentes/CalendarioEscalas';
import CartaoLideranca from '../componentes/CartaoLideranca';
import ModalConta from '../componentes/ModalConta';
import NovoEvento from '../componentes/NovoEvento';
import Layout from '../componentes/Layout';

/**
 * Escala de lideres. O admin monta aqui mesmo, culto por culto; todo lider
 * (de equipe ou de sub) ve a escala inteira e confirma a propria presenca.
 * Quem pode ser escalado nao se cadastra: vem de Equipes.
 */
export default function EscalaLideres() {
  const { usuario } = useAutenticacao();
  const ehAdmin = usuario?.papel === 'admin';

  const [ministerio, setMinisterio] = useState<MinisterioLideranca | null>(null);
  const [eventos, setEventos] = useState<CultoLideranca[]>([]);
  const [semConta, setSemConta] = useState<{ escalaId: number; membroId: number; nome: string }[]>([]);
  const [podeEditar, setPodeEditar] = useState(false);
  const [meuMembroId, setMeuMembroId] = useState<number | null>(null);
  const [lideres, setLideres] = useState<Lider[]>([]);

  const [visao, setVisao] = useState<'lista' | 'calendario'>('lista');
  const [quando, setQuando] = useState<'proximos' | 'anteriores'>('proximos');
  const [erro, setErro] = useState<string | null>(null);
  const [carregado, setCarregado] = useState(false);

  const [abrirAjustes, setAbrirAjustes] = useState(false);
  const [novaFuncao, setNovaFuncao] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [criandoEvento, setCriandoEvento] = useState(false);
  const [criandoContaDe, setCriandoContaDe] = useState<{ id: number; nome: string } | null>(null);
  /** mostrada uma vez, para o admin anotar e entregar; nunca volta do servidor */
  const [senhaEntregar, setSenhaEntregar] = useState<{ nome: string; senha: string } | null>(null);

  // o calendario abre o culto numa janela, sem sair do mes
  const [cultoAberto, setCultoAberto] = useState<CultoLideranca | null>(null);
  const [versaoCalendario, setVersaoCalendario] = useState(0);
  const [erroCulto, setErroCulto] = useState<string | null>(null);

  const { confirmar, perguntar, dialogo } = useConfirmacao();

  const carregar = useCallback(async () => {
    try {
      const [d, l] = await Promise.all([
        buscarEscalaLideres(quando),
        ehAdmin ? buscarLideres() : Promise.resolve({ lideres: [] as Lider[] }),
      ]);
      setMinisterio(d.ministerio);
      setEventos(d.eventos);
      setSemConta(d.semConta ?? []);
      setPodeEditar(d.podeEditar);
      setMeuMembroId(d.meuMembroId);
      setLideres(l.lideres);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar a escala de líderes.');
    } finally {
      setCarregado(true);
    }
  }, [quando, ehAdmin]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const recarregarCulto = useCallback(async () => {
    if (!cultoAberto) return;
    const d = await buscarCultoLideranca(cultoAberto.id);
    setCultoAberto(d.evento);
    setErroCulto(null);
    setVersaoCalendario((v) => v + 1);
    await carregar();
  }, [cultoAberto, carregar]);

  async function abrirCulto(id: number) {
    try {
      const d = await buscarCultoLideranca(id);
      setErroCulto(null);
      setCultoAberto(d.evento);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui abrir este culto.');
    }
  }

  // Quem so olha ve os cultos que ja tem gente da lideranca: vinte cartoes de
  // "ninguem escalado" (limpeza, ensaio) enterrariam o culto em que ele esta.
  const visiveis = useMemo(
    () => (podeEditar ? eventos : eventos.filter((e) => e.total > 0)),
    [eventos, podeEditar],
  );

  const porDia = useMemo(() => {
    const mapa = new Map<string, CultoLideranca[]>();
    for (const e of visiveis) {
      if (!e.inicio) continue;
      const dia = diaSP(e.inicio);
      const lista = mapa.get(dia) ?? [];
      lista.push(e);
      mapa.set(dia, lista);
    }
    return [...mapa.entries()];
  }, [visiveis]);

  const funcoes = ministerio?.funcoes ?? [];
  const aptos = lideres.filter((l) => l.podeEscalar).length;
  const semContaNaLista = lideres.filter((l) => !l.temConta);
  // a mesma pessoa costuma estar em varios cultos: a fila e por pessoa
  const semContaUnicos = [...new Map(semConta.map((p) => [p.membroId, p])).values()];

  async function comAviso(acao: () => Promise<unknown>) {
    setOcupado(true);
    try {
      await acao();
      await carregar();
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui salvar.');
    } finally {
      setOcupado(false);
    }
  }

  const aoErrar = (mensagem: string) => {
    setErro(mensagem);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Layout
      titulo={
        usuario?.papel === 'lider' || usuario?.papel === 'lider_adm'
          ? 'Escala 12 da Igreja'
          : 'Escala de líderes'
      }
      subtitulo={
        podeEditar
          ? 'Monte quem da liderança está em cada culto. Os líderes veem tudo e confirmam.'
          : 'Quem da liderança está em cada culto. Onde estiver o seu nome, confirme.'
      }
      acoes={podeEditar && <Botao onClick={() => setCriandoEvento(true)}>+ Novo evento</Botao>}
    >
      {erro && (
        <div className="mb-4 max-w-3xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}

      {senhaEntregar && (
        <div className="mb-4 max-w-3xl rounded-2xl bg-tinta-100 p-4 ring-1 ring-tinta-200">
          <p className="text-sm text-tinta-900">
            Senha de <strong>{senhaEntregar.nome}</strong>:{' '}
            <code className="rounded bg-white px-2 py-0.5 font-mono">{senhaEntregar.senha}</code>
          </p>
          <p className="mt-1 text-xs text-tinta-400">Anote e entregue agora — ela não aparece de novo.</p>
          <button
            onClick={() => setSenhaEntregar(null)}
            className="mt-2 text-xs text-tinta-400 underline hover:text-tinta-900"
          >
            já anotei
          </button>
        </div>
      )}

      {podeEditar && semContaUnicos.length > 0 && (
        <div className="mb-4 max-w-3xl rounded-2xl bg-amber-50 p-4 ring-1 ring-amber-200">
          <p className="text-sm font-semibold text-amber-900">
            {semContaUnicos.length === 1
              ? '1 líder escalado está sem conta ativa'
              : `${semContaUnicos.length} líderes escalados estão sem conta ativa`}
          </p>
          <p className="mt-1 text-xs text-amber-800">
            Continuam na escala, mas não veem nem confirmam. Reative a conta em Equipe ou troque a pessoa.
          </p>
          <ul className="mt-2 flex flex-wrap gap-2 text-sm text-tinta-900">
            {semContaUnicos.map((p) => (
              <li key={p.membroId} className="rounded-lg bg-white px-2 py-0.5 ring-1 ring-amber-200">
                {p.nome}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-4 flex max-w-3xl flex-wrap items-center gap-2">
        {podeEditar && carregado && (
          <p className="text-sm text-tinta-400">
            <b className="text-tinta-900">{aptos}</b> líderes prontos para escalar
            {semContaNaLista.length > 0 && (
              <>
                {' · '}
                <button
                  onClick={() => setAbrirAjustes(true)}
                  className="font-semibold text-amber-700 underline hover:text-amber-900"
                >
                  {semContaNaLista.length} sem conta
                </button>
              </>
            )}
          </p>
        )}
        <div className="ml-auto flex shrink-0 rounded-xl bg-white p-1 shadow-cartao">
          {(['lista', 'calendario'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setVisao(v)}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
                visao === v ? 'bg-tinta-900 text-white' : 'text-tinta-600 hover:bg-tinta-100'
              }`}
            >
              {v === 'lista' ? 'Lista' : 'Calendário'}
            </button>
          ))}
        </div>
      </div>

      {podeEditar && carregado && funcoes.length === 0 && (
        <div className="mb-4 max-w-3xl">
          <Alerta tipo="aviso">
            A escala de líderes está sem nenhuma função — por isso não aparece onde encaixar as pessoas.
            Abra "Líderes e funções" aqui embaixo e restaure as funções.
          </Alerta>
        </div>
      )}

      {visao === 'calendario' && (
        <CalendarioEscalas
          ministerio="lideranca"
          nomeMinisterio="Liderança"
          buscar={buscarCalendarioLideranca}
          versao={versaoCalendario}
          aoEscolherEvento={(id) => void abrirCulto(id)}
          semEscala={podeEditar ? 'ninguém escalado — toque para montar' : 'ninguém escalado ainda'}
        />
      )}

      {visao === 'lista' && (
        <>
          <div className="mb-4 flex max-w-md rounded-2xl bg-white p-1 shadow-cartao">
            {(
              [
                { valor: 'proximos', rotulo: 'Próximos' },
                { valor: 'anteriores', rotulo: 'Anteriores' },
              ] as const
            ).map((a) => (
              <button
                key={a.valor}
                onClick={() => setQuando(a.valor)}
                className={`flex-1 rounded-xl px-4 py-2 text-sm font-semibold transition ${
                  quando === a.valor ? 'bg-tinta-900 text-white' : 'text-tinta-400 hover:bg-tinta-100'
                }`}
              >
                {a.rotulo}
              </button>
            ))}
          </div>

          {carregado && visiveis.length === 0 && (
            <Cartao className="max-w-3xl">
              <p className="text-sm text-tinta-400">
                {quando === 'anteriores'
                  ? podeEditar
                    ? 'Nenhum culto anterior.'
                    : 'Nenhum culto anterior com escala de líderes.'
                  : podeEditar
                    ? 'Nenhum culto marcado. Crie um com "+ Novo evento" ali em cima.'
                    : 'A escala de líderes dos próximos cultos ainda não foi montada.'}
              </p>
            </Cartao>
          )}

          <div className="max-w-3xl space-y-6">
            {porDia.map(([dia, doDia]) => {
              const primeiro = doDia[0]!;
              return (
                <section key={dia}>
                  <div className="mb-2 flex items-baseline justify-between gap-3 px-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-tinta-900">{numeroDoDia(primeiro.inicio!)}</span>
                      <span className="text-xs font-semibold uppercase tracking-wide text-tinta-400">
                        {mesCurto(primeiro.inicio!)}
                      </span>
                      <span className="text-sm text-tinta-400">· {diaDaSemana(primeiro.inicio!)}</span>
                    </div>
                    <span className="text-sm text-tinta-400">{quandoEmPalavras(primeiro.inicio!)}</span>
                  </div>

                  <div className="space-y-3 border-l-2 border-tinta-200 pl-3">
                    {doDia.map((e) => (
                      <CartaoLideranca
                        key={e.id}
                        evento={e}
                        funcoes={funcoes}
                        lideres={lideres}
                        podeEditar={podeEditar}
                        meuMembroId={meuMembroId}
                        confirmar={confirmar}
                        perguntar={perguntar}
                        aoMudar={carregar}
                        aoErrar={aoErrar}
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}

      {podeEditar && ministerio && (
        <div className="mt-6 max-w-3xl">
          <button
            onClick={() => setAbrirAjustes((a) => !a)}
            className="flex w-full items-center justify-between rounded-2xl bg-white px-5 py-3 text-left shadow-cartao"
          >
            <span className="font-semibold text-tinta-900">Líderes e funções</span>
            <span className="text-sm text-tinta-400">
              {lideres.length} líderes · {funcoes.length} funções {abrirAjustes ? '▾' : '▸'}
            </span>
          </button>

          {abrirAjustes && (
            <div className="mt-3 grid gap-4 md:grid-cols-2">
              <Cartao>
                <h3 className="font-semibold text-tinta-900">Quem pode ser escalado</h3>
                {/* a lista e calculada: cadastrar aqui criaria uma segunda verdade
                    que sai do ar no dia em que alguem troca de lideranca */}
                <p className="mt-1 text-xs text-tinta-400">
                  Vem de Equipes: quem é líder de equipe ou de sub entra aqui sozinho. Para incluir ou tirar
                  alguém, mude a liderança lá.
                </p>
                <ul className="mt-3 max-h-96 space-y-2 overflow-y-auto pr-1 text-sm">
                  {lideres.map((l) => (
                    <li key={l.id} className={l.podeEscalar ? '' : 'opacity-80'}>
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-tinta-800">{l.nome}</span>
                        {l.escalasPelaFrente > 0 && (
                          <span className="shrink-0 rounded-md bg-tinta-100 px-1.5 text-xs text-tinta-600">
                            {l.escalasPelaFrente}× na escala
                          </span>
                        )}
                        {!l.temConta && (
                          <button
                            onClick={() => setCriandoContaDe({ id: l.id, nome: l.nome })}
                            className="ml-auto shrink-0 rounded-lg border border-amber-300 px-2 py-0.5 text-xs text-amber-900 hover:bg-amber-100"
                          >
                            criar conta
                          </button>
                        )}
                        {l.temConta && !l.contaAtiva && (
                          <span className="ml-auto shrink-0 text-xs text-amber-700">conta desativada</span>
                        )}
                      </div>
                      <span className="block truncate text-xs text-tinta-400">{oQueLidera(l)}</span>
                    </li>
                  ))}
                  {lideres.length === 0 && (
                    <li className="text-tinta-400">
                      Ninguém lidera equipe ou sub ainda. Defina os líderes em Equipes.
                    </li>
                  )}
                </ul>
              </Cartao>

              <Cartao>
                <h3 className="font-semibold text-tinta-900">Funções</h3>
                {funcoes.length === 0 ? (
                  <div className="mt-2 rounded-xl bg-amber-50 p-3 text-sm text-amber-800 ring-1 ring-amber-100">
                    <p>Sem função nenhuma, não dá para escalar. Reponha as de sempre:</p>
                    <Botao
                      className="mt-3"
                      carregando={ocupado}
                      onClick={() =>
                        void comAviso(() => api(`/ministerios/${ministerio.id}/funcoes/padrao`, { method: 'POST' }))
                      }
                    >
                      Restaurar funções
                    </Botao>
                  </div>
                ) : (
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {funcoes.map((f) => (
                      <li
                        key={f.id}
                        className="flex items-center gap-1 rounded-full bg-tinta-100 px-3 py-1 text-sm text-tinta-700"
                      >
                        {f.nome}
                        <button
                          onClick={async () => {
                            const ok = await confirmar({
                              titulo: `Tirar a função "${f.nome}"?`,
                              texto: 'Quem já está escalado nela continua escalado; ela só deixa de aparecer para novos cultos.',
                              confirmar: 'Tirar função',
                              perigo: true,
                            });
                            if (ok) void comAviso(() => removerFuncao(f.id));
                          }}
                          className="text-tinta-400 hover:text-red-600"
                          title="Tirar da lista"
                          aria-label={`Tirar a função ${f.nome}`}
                        >
                          &times;
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <input
                  value={novaFuncao}
                  onChange={(e) => setNovaFuncao(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && novaFuncao.trim()) {
                      void comAviso(async () => {
                        await criarFuncao(ministerio.id, novaFuncao.trim());
                        setNovaFuncao('');
                      });
                    }
                  }}
                  placeholder="Nova função e Enter"
                  className="mt-3 w-full rounded-xl border border-tinta-200 px-3 py-2 text-sm"
                />
              </Cartao>
            </div>
          )}
        </div>
      )}

      {cultoAberto && (
        <Modal
          titulo={`${cultoAberto.titulo}${cultoAberto.inicio ? ` · ${diaDaSemana(cultoAberto.inicio)} ${horaSP(cultoAberto.inicio)}` : ''}`}
          aoFechar={() => setCultoAberto(null)}
        >
          {erroCulto && (
            <div className="mt-3">
              <Alerta>{erroCulto}</Alerta>
            </div>
          )}
          <div className="mt-3">
            <CartaoLideranca
              evento={cultoAberto}
              funcoes={funcoes}
              lideres={lideres}
              podeEditar={podeEditar}
              meuMembroId={meuMembroId}
              confirmar={confirmar}
              perguntar={perguntar}
              aoMudar={recarregarCulto}
              aoErrar={setErroCulto}
              semCabecalho
              semMoldura
              comecarAberto
            />
          </div>
          <div className="mt-4 flex justify-end">
            <Botao variante="secundario" onClick={() => setCultoAberto(null)}>
              Fechar
            </Botao>
          </div>
        </Modal>
      )}

      {criandoContaDe && (
        <ModalConta
          pessoa={criandoContaDe}
          criar={(dados) => criarContaDeLider(criandoContaDe.id, dados)}
          aoFechar={() => setCriandoContaDe(null)}
          aoCriar={(senha) => {
            setSenhaEntregar({ nome: criandoContaDe.nome, senha });
            setCriandoContaDe(null);
            void carregar();
          }}
        />
      )}

      {criandoEvento && (
        <NovoEvento
          diaSugerido={diaSP(new Date().toISOString())}
          aoFechar={() => setCriandoEvento(false)}
          aoCriar={async () => {
            setCriandoEvento(false);
            await carregar();
          }}
        />
      )}

      {dialogo}
    </Layout>
  );
}
